import React, { useState } from 'react';
import PublicNav from '../../components/PublicNav';
import { BackToHome } from '../../components/BackToHome';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { privacyAPI } from '../../lib/api';

const REQUEST_TYPES = [
  { value: 'access', label: 'Access my data' },
  { value: 'delete', label: 'Delete my data' },
  { value: 'correct', label: 'Correct my data' },
  { value: 'portability', label: 'Data portability (export)' },
  { value: 'object', label: 'Object to / restrict processing' },
  { value: 'withdraw_consent', label: 'Withdraw consent' },
];

const REGIONS = ['India (DPDP)', 'EU/UK (GDPR)', 'USA (BIPA/CCPA)', 'UAE/Singapore (PDPL/PDPA)', 'Other'];

const inputStyle = { width: '100%', padding: '0.75rem', borderRadius: 10, border: '1px solid #CBD5E1', background: '#FAF7F2', color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3', marginBottom: '0.375rem' };

export default function DataRequestPage() {
  const [form, setForm] = useState({ name: '', email: '', request_type: 'access', region: REGIONS[0], details: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and email'); return;
    }
    setSubmitting(true);
    try {
      const res = await privacyAPI.submitDataRequest(form);
      setDone(res.data.id);
      toast.success('Your request has been submitted.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }} data-testid="data-request-page">
      <PublicNav />
      <BackToHome testid="back-to-home-top" />
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '3rem 2rem 4rem' }}>
        <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2.25rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.5rem' }}>Data Request</h1>
        <p style={{ fontSize: '0.9375rem', color: '#718096', marginBottom: '2rem' }}>
          Submit a request to access, correct, delete, or export your personal data, or to withdraw consent. Our Data Protection team will respond within the timeframe required by applicable law (e.g., GDPR within 30 days).
        </p>

        {done ? (
          <div data-testid="data-request-success" style={{ padding: '2rem', borderRadius: 14, border: '1px solid #BBF7D0', background: '#F0FDF4' }}>
            <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: '#166534', marginBottom: '0.5rem' }}>Request received</h2>
            <p style={{ fontSize: '0.875rem', color: '#15803D', margin: 0 }}>
              Thank you. Your reference number is <strong>{done}</strong>. We've emailed a confirmation to <strong>{form.email}</strong> and our Data Protection Officer has been notified.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label style={labelStyle}>Full name</label>
              <input data-testid="dr-name" style={inputStyle} value={form.name} onChange={set('name')} placeholder="Your full name" />
            </div>
            <div>
              <label style={labelStyle}>Email (must match your account/records)</label>
              <input data-testid="dr-email" type="email" style={inputStyle} value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </div>
            <div>
              <label style={labelStyle}>Request type</label>
              <select data-testid="dr-type" style={inputStyle} value={form.request_type} onChange={set('request_type')}>
                {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Your region</label>
              <select data-testid="dr-region" style={inputStyle} value={form.region} onChange={set('region')}>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Additional details (optional)</label>
              <textarea data-testid="dr-details" style={{ ...inputStyle, minHeight: 110, resize: 'vertical' }} value={form.details} onChange={set('details')} placeholder="Anything that will help us locate and action your request" />
            </div>
            <Button data-testid="dr-submit" type="submit" disabled={submitting} style={{ background: submitting ? '#94A3B8' : '#0076A3', color: '#fff', borderRadius: 999, padding: '0.875rem', fontWeight: 600, fontSize: '0.9375rem' }}>
              {submitting ? 'Submitting…' : 'Submit Request'}
            </Button>
            <p style={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', margin: 0 }}>
              We may need to verify your identity before fulfilling certain requests to protect your data.
            </p>
          </form>
        )}
      </div>
      <BackToHome testid="back-to-home-bottom" style={{ paddingBottom: '2.5rem' }} />
    </div>
  );
}
